import { Asset } from '@cube3/common/model/schema/resources/asset';
import { Generation } from '@cube3/common/model/schema/resources/generation';
import React, { createContext, useContext, useMemo, useState } from 'react';
import { useModalActions } from '../../Modals/modalActions';
import { getPrompt } from '../utils/buildGenerationTree';

type ExtraConfig = {
  saved?: boolean;
  /** Cached image that has been converted to a file type, to prevent issues caused by expiration */
  file?: File;
  /** If the generation is the shown image */
  active?: boolean;
  base64?: { src: string; name: string };
};

export type ExtendedGeneration = Generation & ExtraConfig;

type GenerativeContext = {
  /** The selected assets will be used as reference */
  selections: Asset[];
  /** Cached generations */
  generations: ExtendedGeneration[];
  /** The current generated image to display */
  activeGeneration: ExtendedGeneration;
  canSelectMore: boolean;
  prefilledPrompt: string;
  /** Add or remove assets to selections */
  setSelections: (asset) => void;
  setActiveGeneration: (ge) => void;
  /** Store the generation to the state */
  saveGeneration: (ge) => void;
  /** Callback to check if an asset is selected */
  isAssetSelected: (a) => boolean;
  /** Go back to the previous generation -> selected assets -> start page */
  goBack: () => void;
  /** Set the `saved` attribute to `true` if the target image has been saved to cube */
  updateGeneration: (ge, config: {}) => void;
};

const initialContext: GenerativeContext = {
  selections: [],
  canSelectMore: true,
  generations: [],
  activeGeneration: undefined,
  prefilledPrompt: '',
  setActiveGeneration: null,
  setSelections: null,
  saveGeneration: null,
  isAssetSelected: null,
  goBack: null,
  updateGeneration: null
};

export const generativeContext =
  createContext<GenerativeContext>(initialContext);

const MAX_SELECTION = 2 as const;
const emptyArray = [];

export const GenerativeContextProvider = (props) => {
  const [selections, setSelectionsState] = useState(emptyArray);
  const [generations, setGenerationsState] = useState(emptyArray);
  const [prevSelected, setPrevSelected] = useState(null);

  const { previousModal } = useModalActions();

  const selectionState = useMemo(() => {
    return {
      selections,
      canSelectMore: selections.length < MAX_SELECTION,
      setSelections: (asset) => {
        if (Array.isArray(asset)) {
          setSelectionsState(asset);
          return;
        }
        const existed = !!selections.find((s) => s.id === asset.id);
        if (existed) {
          setSelectionsState(selections.filter((s) => s.id !== asset.id));
        } else {
          setSelectionsState([...selections, asset]);
        }
      },
      isAssetSelected: (a) => selections.find((s) => s.id === a.id)
    };
  }, [selections]);

  const ctx = useMemo(() => {
    const activeGeneration = generations.find((g) => g.active);

    const setActiveGeneration = (ge) => {
      setGenerationsState((prev) =>
        prev.map((p) => {
          if (p.active) {
            setPrevSelected(p);
          }
          return {
            ...p,
            active: ge && p.id === ge.id
          };
        })
      );
    };

    return {
      ...selectionState,
      generations,
      activeGeneration,
      prefilledPrompt: getPrompt(generations, activeGeneration, prevSelected),
      setActiveGeneration,
      saveGeneration: (item: Generation) => {
        setGenerationsState((prev) => {
          return [
            // clear active state for the rest of images
            ...prev.map((p) => ({ ...p, active: false })),
            // set the image to active
            { ...item, active: true }
          ];
        });
      },
      goBack: () => {
        const currentIndex = generations.findIndex(
          (g) => g.id === activeGeneration?.id
        );
        if (!activeGeneration) {
          previousModal();
        } else {
          if (currentIndex <= 0) {
            // to display the selected assets
            setActiveGeneration(null);
          } else {
            // go back to the previous generation
            setActiveGeneration(generations[currentIndex - 1]);
          }
        }
      },
      updateGeneration: (t, config: ExtraConfig) =>
        setGenerationsState((prev) =>
          prev.map((p) => (p.id === t.id ? { ...p, ...config } : p))
        )
    };
  }, [selectionState, prevSelected, generations]);

  return (
    <generativeContext.Provider value={ctx}>
      {props.children}
    </generativeContext.Provider>
  );
};

export const useGenerativeContext = () => {
  return useContext(generativeContext);
};
